import arriereBackoffice, {
  CaseRecordType,
  ReviewInfo,
  UUID,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Alert } from '@avantstay/backoffice-core'

import { WorkOrderItemCountQueryResult } from '@new/domains/operations/screens/WorkOrderReview/WorkOrderReview.types'

export const getWorkOrderApprovedCount = async (
  workOrderId: string,
): Promise<WorkOrderItemCountQueryResult> => {
  return arriereBackoffice.queries.workOrders({
    __alias: 'getWorkOrderApprovedCount',
    countItems: {
      __args: {
        workOrderId,
      },
      approved: true,
      completedWithBlock: true,
    },
  })
}

export const getWorkOrderById = async (id: string) => {
  const workOrder = await arriereBackoffice.queries.workOrders({
    __alias: 'operations_workOrder_getWorkOrderById',
    find: {
      __args: {
        workOrderId: id,
      },
      id: true,
      description: true,
      managedUnitId: true,
      sfdcCaseNumber: true,
      workOrderType: true,
      forcedApproveAvailable: true,
      reason: true,
      workOrderSkip: {
        walkthroughDescription: true,
      },
      reportedIssues: {
        caseType: true,
        category: true,
        subcategory: true,
        createdAt: true,
        locationInHome: true,
        subject: true,
        reporterName: true,
        description: true,
        sfdcCaseNumber: true,
        workOrderId: true,
        reportedFromItemInfo: {
          itemId: true,
          itemTitle: true,
          sectionId: true,
          sectionTitle: true,
        },
        issuePhotos: {
          url: true,
          id: true,
        },
        sfdcCaseUrl: true,
      },
      sections: {
        id: true,
        templateSectionId: true,
        workOrderId: true,
        title: true,
        items: {
          id: true,
          description: true,
          forPets: true,
          instructions: true,
          notes: true,
          photoUploadRequired: true,
          referenceImageId: true,
          expectedNegativeAnswer: true,
          answers: true,
          answerType: true,
          answerOptions: {
            answerId: true,
            label: true,
            expected: true,
          },
          review: {
            reviewer: {
              name: true,
            },
            dateTime: true,
            reason: true,
          },
          referencePhoto: {
            url: true,
            id: true,
          },
          templateItemId: true,
          imageId: true,
          reviewStatus: true,
          workStatus: true,
          kind: true,
          approvedAt: true,
          itemPhoto: {
            url: true,
            id: true,
          },
          itemPhotos: {
            id: true,
            url: true,
            createdAt: true,
          },
        },
        reviewStatus: true,
        createdAt: true,
        updatedAt: true,
        stats: {
          workPercentage: true,
          readyForReviewPercentage: true,
        },
      },
      status: true,
      taskType: true,
      reviewStatus: true,
      inspectorNotes: true,
      stats: {
        dueDate: true,
        workMeanPercentage: true,
        photosMeanPercentage: true,
        initialWorkTime: true,
        lastUpdate: true,
        finalWorkTime: true,
      },
      createdAt: true,
      updatedAt: true,
      lastReviewer: {
        name: true,
      },
      home: {
        id: true,
        name: true,
        thumbnail: {
          url: true,
        },
        region: {
          id: true,
          name: true,
        },
      },
      workers: {
        vendorId: true,
        fullName: true,
        phone: true,
      },
      vendor: {
        id: true,
        name: true,
      },
      tags: true,
    },
  })

  return workOrder.find
}

export const forceApproveWorkOrder = async (workOrderId: string): Promise<ReviewInfo> =>
  arriereBackoffice.mutations
    .workOrders({
      forceApprove: {
        __args: {
          workOrderId,
        },
        reviewer: {
          name: true,
        },
        dateTime: true,
      },
    })
    .then(({ forceApprove }) => forceApprove)

export const acceptWorkOrderItem = async (itemId: string): Promise<ReviewInfo> =>
  arriereBackoffice.mutations
    .checklistItems({
      accept: {
        __args: {
          itemId,
        },
        reviewer: {
          name: true,
        },
        dateTime: true,
        reason: true,
      },
    })
    .then(({ accept }) => accept)

export const rejectWorkOrderItem = async (itemId: string, reason?: string): Promise<ReviewInfo> =>
  arriereBackoffice.mutations
    .checklistItems({
      reject: {
        __args: { itemId, reason },
        reviewer: {
          name: true,
        },
        dateTime: true,
      },
    })
    .then(({ reject }) => reject)

export const makePictureAsReference = async ({
  templateItemId,
  templateSectionId,
  assetId,
  homeId,
  itemId,
}: {
  itemId: string
  templateItemId: string
  templateSectionId: string
  assetId: string
  homeId: UUID
}): Promise<string> => {
  // this updates Work Orders Templates Management BFF DB
  try {
    arriereBackoffice.mutations.setHomeItemReferencePhoto({
      __args: {
        itemId: templateItemId,
        sectionId: templateSectionId,
        homeId,
        assetId,
      },
      referencePhoto: {
        url: true,
      },
    })
  } catch (e) {
    console.error(e)
  }

  // below is to update existing templates on scala BE
  return arriereBackoffice.mutations
    .checklistItems({
      makePictureAsReference: {
        __args: {
          itemId,
          assetId,
        },
        url: true,
      },
    })
    .then(result => result.makePictureAsReference.url)
}

export const getSfdcStatus = async (recordType: CaseRecordType) => {
  return arriereBackoffice.queries
    .cases({
      __alias: 'getSfdcStatus',
      getCaseInputsV2: {
        __args: {
          recordTypes: [recordType],
        },
        inputs: {
          statuses: {
            label: true,
            apiName: true,
          },
        },
      },
    })
    .then(results => {
      return results.getCaseInputsV2
    })
    .catch(error => {
      Alert.Error((error as Error).message)
      return undefined
    })
}
